import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

export interface UserWithRole {
  id: string;
  name: string | null;
  email: string;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  role?: string;
  tenant_id?: string;
  unit_id?: string | null;
}

export const useUsers = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['users', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get users in the same tenant with their private details
      const { data: publicData, error: publicError } = await supabase
        .from('users_public')
        .select(`
          *,
          user_roles (
            role,
            tenant_id,
            unit_id
          )
        `)
        .order('created_at', { ascending: false });

      if (publicError) throw publicError;

      // Fetch private details (emails) separately - RLS will only return visible ones
      const userIds = publicData.map(u => u.id);
      const { data: privateData, error: privateError } = await supabase
        .from('user_private_details')
        .select('user_id, email')
        .in('user_id', userIds);

      if (privateError) throw privateError;

      // Create a map for quick email lookup
      const emailMap = new Map(privateData?.map(p => [p.user_id, p.email]) || []);
      
      // Transform the data to include role information and email
      return publicData.map(user => ({
        ...user,
        email: emailMap.get(user.id) || 'hidden@example.com', // Fallback if email not visible
        role: user.user_roles?.[0]?.role || 'viewer',
        tenant_id: user.user_roles?.[0]?.tenant_id,
        unit_id: user.user_roles?.[0]?.unit_id,
      })) as UserWithRole[];
    },
    enabled: !!user?.id,
  });

  const inviteUserMutation = useMutation({
    mutationFn: async ({ email, name, role, tenantId, inviterName }: { 
      email: string; 
      name: string; 
      role: string;
      tenantId: string;
      inviterName: string;
    }) => {
      // Use the invite-user edge function
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email,
          name,
          role,
          tenantId,
          inviterName
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "User invited",
        description: "Invitation sent successfully with welcome email",
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to invite user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, role, tenantId, unitId }: { 
      userId: string; 
      role: string; 
      tenantId: string; 
      unitId?: string | null; 
    }) => {
      const { data, error } = await supabase
        .from('user_roles')
        .upsert({
          user_id: userId,
          role: role as any,
          tenant_id: tenantId,
          unit_id: unitId,
        });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "User role updated",
        description: "Role has been updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update role",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateUserProfileMutation = useMutation({
    mutationFn: async ({ userId, name, email }: { userId: string; name: string; email: string }) => {
      // Update name in users_public
      const { error: profileError } = await supabase
        .from('users_public')
        .update({ name })
        .eq('id', userId);
      
      if (profileError) throw profileError;

      // Update email in user_private_details
      const { error: emailError } = await supabase
        .from('user_private_details')
        .update({ email })
        .eq('user_id', userId);
      
      if (emailError) throw emailError;
    },
    onSuccess: () => {
      toast({
        title: "Profile updated",
        description: "User profile has been updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    users,
    isLoading,
    error,
    inviteUser: inviteUserMutation.mutate,
    updateUserRole: updateUserRoleMutation.mutate,
    updateUserProfile: updateUserProfileMutation.mutate,
    isInviting: inviteUserMutation.isPending,
    isUpdatingRole: updateUserRoleMutation.isPending,
    isUpdatingProfile: updateUserProfileMutation.isPending,
  };
};